import { useEffect, useRef, useState } from 'react';

// Pull down on a scrolled-to-top container to refresh. Returns { pull, progress, ready, refreshing } for drawing the indicator:
//   pull      - how far (px) the indicator has travelled; it eases out so it never runs away from the finger
//   progress  - 0..1 of the way to the trigger distance (half the screen height of finger travel)
//   ready     - true once progress reaches 1; releasing then refreshes
// The long distance means ordinary scrolling or a small slip never triggers it.
const DEAD_ZONE = 8;        // finger travel before the gesture counts as a pull, so taps and small slips do nothing
const MAX_INDICATOR = 72;   // px the indicator travels at most
const TRIGGER_SCREEN = 0.5; // fraction of the screen height the finger must travel
const SETTLE_PULL = 48;     // where the indicator rests while refreshing

export default function usePullToRefresh(containerRef, onRefresh, enabled = true) {
  const [state, setState] = useState({ pull: 0, progress: 0 });
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const startX = useRef(0);
  const pulling = useRef(false);
  const progressRef = useRef(0);
  const frame = useRef(0);
  const busy = useRef(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !enabled) return undefined;

    // one state update per animation frame keeps the indicator smooth instead of re-rendering on every touch event
    const draw = (progress) => {
      progressRef.current = progress;
      if (frame.current) return;
      frame.current = requestAnimationFrame(() => {
        frame.current = 0;
        const p = progressRef.current;
        // ease-out curve: moves with the finger at first, then slows as it nears the trigger
        const pull = MAX_INDICATOR * (1 - Math.pow(1 - Math.min(p, 1), 2));
        setState({ pull, progress: p });
      });
    };
    const reset = () => {
      progressRef.current = 0;
      if (frame.current) { cancelAnimationFrame(frame.current); frame.current = 0; }
      setState({ pull: 0, progress: 0 });
    };

    // A list inside the container that is itself scrolled down owns the gesture: pulling it back up is not a refresh
    const innerScrolled = (target) => {
      for (let node = target; node && node !== el; node = node.parentElement) {
        if (node.scrollTop > 0) return true;
      }
      return false;
    };
    const onStart = (e) => {
      if (busy.current || el.scrollTop > 0 || e.touches.length !== 1 || innerScrolled(e.target)) { startY.current = null; return; }
      startY.current = e.touches[0].clientY;
      startX.current = e.touches[0].clientX;
      pulling.current = false;
    };
    const onMove = (e) => {
      if (startY.current === null || busy.current) return;
      const dy = e.touches[0].clientY - startY.current;
      const dx = Math.abs(e.touches[0].clientX - startX.current);
      if (!pulling.current) {
        // mostly-sideways or upward movement is normal scrolling / swiping - leave it alone
        if (dy < 0 || dx > dy) { if (dy < -DEAD_ZONE) startY.current = null; return; }
        if (dy < DEAD_ZONE) return;
        pulling.current = true;
      }
      if (el.scrollTop > 0) { reset(); return; }
      // we own this gesture: stop iOS from rubber-banding the whole page (which exposes the dark background)
      if (e.cancelable) e.preventDefault();
      const distance = window.innerHeight * TRIGGER_SCREEN;
      draw(Math.min(1.15, Math.max(0, (dy - DEAD_ZONE) / distance)));
    };
    // the finger lifting after a pull would otherwise "tap" whatever card or button is under it
    const swallowClick = (e) => { e.stopPropagation(); e.preventDefault(); };
    const onEnd = async () => {
      if (startY.current === null) return;
      startY.current = null;
      const wasPulling = pulling.current;
      pulling.current = false;
      if (wasPulling) {
        el.addEventListener('click', swallowClick, { capture: true, once: true });
        setTimeout(() => el.removeEventListener('click', swallowClick, { capture: true }), 400);
      }
      const reached = progressRef.current >= 1;
      if (!reached || busy.current) { reset(); return; }
      busy.current = true;
      progressRef.current = 0;
      if (frame.current) { cancelAnimationFrame(frame.current); frame.current = 0; }
      setRefreshing(true);
      setState({ pull: SETTLE_PULL, progress: 1 });
      try { await onRefresh(); } catch (err) { /* the page reloads or reports its own errors */ }
      // keep the spinner visible for a moment so the refresh reads as done
      setTimeout(() => { busy.current = false; setRefreshing(false); setState({ pull: 0, progress: 0 }); }, 600);
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      if (frame.current) cancelAnimationFrame(frame.current);
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [containerRef, onRefresh, enabled]);

  return { pull: state.pull, progress: state.progress, ready: state.progress >= 1, refreshing };
}
